import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Spinner,
  useTheme,
  useColorMode,
  Select,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";

import {
  faChevronLeft,
  faChevronRight,
  faSearch,
  faClipboardList,
} from "@fortawesome/free-solid-svg-icons";
import { cqAPI, loadCustomers } from "../../lib/Auth";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../CallReport/CallReport.css";
import AsyncSelect from "react-select/async";
import { components } from "react-select";
import debounce from "lodash.debounce";

function POMonitoring() {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState("Today");
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const [dateRange, setDateRange] = useState([
    moment().startOf("day").toDate(),
    moment().endOf("day").toDate(),
  ]);
  const [startDate, endDate] = dateRange;
  const theme = useTheme();
  const { colorMode } = useColorMode();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const bid = selectedLocation?._id?.toString() || "";

  const updateUrlHash = (startDate, endDate, selectedRange, bid) => {
    const queryParams = new URLSearchParams({
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
      selectedRange,
      bid,
    }).toString();
    window.location.hash = queryParams;
  };

  const sortedSessions = useMemo(() => {
    let sortableItems = [...sessions];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        const va =
          sortConfig.key === "created_date"
            ? new Date(a[sortConfig.key])
            : a[sortConfig.key];
        const vb =
          sortConfig.key === "created_date"
            ? new Date(b[sortConfig.key])
            : b[sortConfig.key];
        if (va < vb) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (va > vb) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [sessions, sortConfig]);

  const fetchSessions = async (params = {}) => {
    setIsLoading(true);

    try {
      const formattedStartDate = params.startDate
        ? moment(params.startDate).format("L")
        : moment(dateRange[0]).format("L");
      const formattedEndDate = params.endDate
        ? moment(params.endDate).format("L")
        : moment(dateRange[1]).format("L");
      const bidParam = params.bid || bid;

      const response = await cqAPI.get(
        `/monitoring/po-sessions?rangeBeg=${formattedStartDate}&rangeEnd=${formattedEndDate}&bid=${bidParam}`
      );

      setSessions(response.data.sessions);

      if (response.data.business) {
        setSelectedLocation(response.data.business);
      }
    } catch (error) {
      console.error("Error fetching PO sessions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadOptions = useCallback(
    debounce((inputValue, callback) => {
      loadCustomers(inputValue).then((options) => callback(options));
    }, 500),
    []
  );

  const DropdownIndicator = (props) => {
    return (
      components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
          <FontAwesomeIcon icon={faSearch} />
        </components.DropdownIndicator>
      )
    );
  };

  const formatLocationOption = (option) => (
    <Flex direction="column" py={1}>
      <Flex align="center">
        <Text>{option.name}</Text>
      </Flex>
    </Flex>
  );

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleDateRangeSelection = (value) => {
    setSelectedRange(value);
    let start, end;
    switch (value) {
      case "Today":
        start = moment().startOf("day");
        end = moment().endOf("day");
        break;
      case "Yesterday":
        start = moment().subtract(1, "days").startOf("day");
        end = moment().subtract(1, "days").endOf("day");
        break;
      case "Last 7 days":
        start = moment().subtract(6, "days").startOf("day");
        end = moment().endOf("day");
        break;
      case "Last 4 weeks":
        start = moment().subtract(4, "weeks").startOf("day");
        end = moment().endOf("day");
        break;
      case "Month to Date":
        start = moment().startOf("month");
        end = moment().endOf("day");
        break;
      case "Quarter to date":
        start = moment().startOf("quarter");
        end = moment().endOf("day");
        break;
      case "Year to date":
        start = moment().startOf("year");
        end = moment().endOf("day");
        break;
      case "Custom":
      default:
        start = null;
        end = null;
    }
    setDateRange([start ? start.toDate() : null, end ? end.toDate() : null]);
  };

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <Flex
      direction="column"
      width="100%"
      paddingLeft="20px"
      paddingRight="20px"
    >
      <Flex justifyContent="center" alignItems="center" mt={4}>
        <Box
          p={2}
          borderRadius="md"
          borderWidth="2px"
          borderColor="purple.500"
          mr={2}
        >
          {startDate ? startDate.toLocaleDateString() : "Start Date"}
        </Box>
        <Text mx={2}>→</Text>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500">
          {endDate ? endDate.toLocaleDateString() : "End Date"}
        </Box>
      </Flex>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        paddingTop="10px"
      >
        <Box
          as="button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </Box>
        <Box>
          {date.toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}
        </Box>
        <Box
          as="button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </Box>
      </Flex>
    </Flex>
  );

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const startDate = hashParams.get("startDate")
      ? moment(hashParams.get("startDate")).toDate()
      : moment().startOf("day").toDate();
    const endDate = hashParams.get("endDate")
      ? moment(hashParams.get("endDate")).toDate()
      : moment().endOf("day").toDate();
    const selectedRange = hashParams.get("selectedRange") || "Today";
    const bid = hashParams.get("bid") || "";

    setDateRange([startDate, endDate]);
    setSelectedRange(selectedRange);

    fetchSessions({ startDate, endDate, selectedRange, bid });
  }, []);

  useEffect(() => {
    updateUrlHash(startDate, endDate, selectedRange, bid);
  }, [startDate, endDate, selectedRange, bid]);

  const navigateToSession = (sessionId) => {
    navigate(`/monitoring/po-sessions/${sessionId}`);
  };

  if (isLoading) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        height="100vh"
        width="100%"
        position="relative"
      >
        <Spinner className="centered-spinner" color="blue.500" size="xl" />
      </Flex>
    );
  }

  const hoverBgColor =
    colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <Flex justifyContent="flex-start" mb={4} wrap="wrap">
        <Flex align="center" mr={2} mb={2}>
          <Select
            placeholder="Select range"
            value={selectedRange}
            onChange={(e) => handleDateRangeSelection(e.target.value)}
            className="date-type"
          >
            <option value="Today">Today</option>
            <option value="Yesterday">Yesterday</option>
            <option value="Last 7 days">Last 7 days</option>
            <option value="Last 4 weeks">Last 4 weeks</option>
            <option value="Month to Date">Month to Date</option>
            <option value="Quarter to date">Quarter to date</option>
            <option value="Year to date">Year to date</option>
            <option value="Custom">Custom</option>
          </Select>
        </Flex>
        <Flex align="center" mr={2} mb={2}>
          <DatePicker
            className={`custom-datepicker ${
              colorMode === "light" ? "light-theme" : "dark-theme"
            }`}
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => {
              setDateRange(update);
              setSelectedRange("Custom");
            }}
            isClearable={true}
            renderCustomHeader={renderCustomHeader}
          />
        </Flex>
        <Flex align="center" mr={2} mb={2}>
          <AsyncSelect
            loadOptions={loadOptions}
            value={selectedLocation}
            formatOptionLabel={formatLocationOption}
            placeholder="Search Locations"
            components={{ DropdownIndicator }}
            className="search-input"
            id="search-input"
            isClearable
            styles={{
              container: (base) => ({ ...base, minWidth: "180px" }),
            }}
            onChange={(opt) => setSelectedLocation(opt)}
          />
        </Flex>
        <Flex ml="auto" mb={2}>
          <Button
            leftIcon={<FontAwesomeIcon icon={faClipboardList} />}
            onClick={fetchSessions}
          >
            Show sessions
          </Button>
        </Flex>
      </Flex>
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th onClick={() => requestSort("created_date")} cursor="pointer">
              Date
            </Th>
            <Th onClick={() => requestSort("business_name")} cursor="pointer">
              Business
            </Th>
            <Th onClick={() => requestSort("num_queries")} cursor="pointer">
              Queries
            </Th>
            <Th onClick={() => requestSort("overall_quality")} cursor="pointer">
              Overall Quality
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {sortedSessions.map((session) => (
            <Tr
              key={session._id}
              _hover={{ bg: hoverBgColor, cursor: "pointer" }}
              onClick={() => navigateToSession(session.session_id)}
            >
              <Td>{session.created}</Td>
              <Td>{session.business_name}</Td>
              <Td>{session.num_queries}</Td>
              <Td>{session.overall_quality}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

export default POMonitoring;